import type { User } from '@clerk/clerk-react';
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { User as EZHOAUser } from '../../types';

interface ClerkAuthState {
  ezhoaUser: EZHOAUser | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: ClerkAuthState = {
  ezhoaUser: null,
  isLoading: false,
  error: null,
};

// Helper to convert Clerk user to EZHOA user
export const clerkToEZHOAUser = (clerkUser: User | null | undefined): EZHOAUser | null => {
  if (!clerkUser) return null;

  // Get role from public metadata (we'll set this in Clerk dashboard or during signup)
  const role = (clerkUser.publicMetadata?.role as 'admin' | 'homeowner' | 'vendor') || 'homeowner';
  const hoaId = (clerkUser.publicMetadata?.hoaId as string) || 'hoa-001';
  const permissions = getPermissionsForRole(role);

  return {
    id: clerkUser.id,
    name: clerkUser.fullName || clerkUser.firstName || 'User',
    email: clerkUser.primaryEmailAddress?.emailAddress || '',
    role,
    phone: clerkUser.primaryPhoneNumber?.phoneNumber || '',
    hoaId,
    createdAt: clerkUser.createdAt
      ? new Date(clerkUser.createdAt).toISOString()
      : new Date().toISOString(),
    address: (clerkUser.publicMetadata?.address as string) || '',
    permissions,
  };
};

const getPermissionsForRole = (role: 'admin' | 'homeowner' | 'vendor'): string[] => {
  switch (role) {
    case 'admin':
      return ['manage_users', 'manage_vendors', 'view_analytics', 'manage_finances'];
    case 'vendor':
      return ['view_jobs', 'submit_bids', 'manage_profile'];
    case 'homeowner':
      return ['submit_requests', 'view_documents', 'view_calendar'];
    default:
      return [];
  }
};

const clerkAuthSlice = createSlice({
  name: 'clerkAuth',
  initialState,
  reducers: {
    setEZHOAUser: (state, action: PayloadAction<EZHOAUser | null>) => {
      state.ezhoaUser = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    clearAuth: (state) => {
      state.ezhoaUser = null;
      state.isLoading = false;
      state.error = null;
    },
  },
});

export const { setEZHOAUser, setLoading, setError, clearAuth } = clerkAuthSlice.actions;
export default clerkAuthSlice.reducer;
